import { theme } from '@kijiji/theme'
import { useDecision } from '@optimizely/react-sdk'
import dynamic from 'next/dynamic'
import { useSession } from 'next-auth/react'
import { useCallback, useMemo } from 'react'

import { SrpResults } from '@/components/srp/results-sort/results'
import {
  SaveSearchWrapper,
  SrpResultsSortContainer,
  SrpSortContainer,
  SrpSortMapContainer,
} from '@/components/srp/results-sort/styled'
import { SaveSearchChipButton } from '@/components/srp/save-search-chip'
import { isUserAuthenticated } from '@/features/auth/constants/user'
import { MapViewButton } from '@/features/map/components/map-view-button/MapViewButton'
import { GA_SAVE_SEARCH_TYPES } from '@/lib/ga/constants/ga'
import { FEATURE_FLAG } from '@/lib/optimizely'
import { SelectVariant } from '@/ui/molecules/select'
import { getUserDevice } from '@/utils/getUserDevice'

const SrpSort = dynamic(() => import('./sort').then((mod) => mod.SrpSort), { ssr: false })

export type SrpResultsSortProps = {
  /** This prop should be used in order to indicates whether the results text should be shown or not */
  shouldRenderResultsSortText?: boolean
  setShouldRenderMapSRP?: React.Dispatch<React.SetStateAction<boolean>>
  shouldRenderMapSRP?: boolean
}

export const SrpResultsSort = ({
  shouldRenderResultsSortText = true,
  setShouldRenderMapSRP,
  shouldRenderMapSRP = false,
}: SrpResultsSortProps) => {
  const [srpQuickFilterPillsDecision] = useDecision(FEATURE_FLAG.SRP_QUICK_FILTERS_PILLS)
  const areQuickFilterPillsActive =
    srpQuickFilterPillsDecision?.enabled && srpQuickFilterPillsDecision?.variationKey === 'b'
  const [saveSearchWithinFiltersDecision] = useDecision(FEATURE_FLAG.SAVED_SEARCH_WITHIN_FILTERS)
  const { status: userSessionStatus } = useSession()
  const isMobileDevice = getUserDevice().isPhone

  const isSaveSearchBtnEnabled =
    saveSearchWithinFiltersDecision.enabled &&
    isUserAuthenticated(userSessionStatus) &&
    isMobileDevice

  const handleRenderMapView = useCallback(() => {
    setShouldRenderMapSRP?.((oldState) => !oldState)
  }, [setShouldRenderMapSRP])

  const shouldRenderListToggle = useMemo(
    () => !shouldRenderResultsSortText,
    [shouldRenderResultsSortText]
  )

  return (
    <SrpResultsSortContainer
      flexDirection="row"
      isSaveSearchActive={
        saveSearchWithinFiltersDecision.enabled && isUserAuthenticated(userSessionStatus)
      }
    >
      {isSaveSearchBtnEnabled && (
        <SaveSearchWrapper justifyContent="flex-start">
          <SaveSearchChipButton
            isPillVariant={areQuickFilterPillsActive}
            isSrpView
            eventTrackingSource={GA_SAVE_SEARCH_TYPES.btn}
          />
        </SaveSearchWrapper>
      )}
      {shouldRenderResultsSortText && <SrpResults showOnLargeBreakpoint />}
      <SrpSortMapContainer gap={theme.spacing.defaultSmall} alignItems="center">
        {shouldRenderMapSRP && (
          <MapViewButton
            onClick={handleRenderMapView}
            type={shouldRenderListToggle ? 'list' : 'map'}
          />
        )}
        <SrpSortContainer isSaveSearchActive={isSaveSearchBtnEnabled} isRoundedSelect={false}>
          <SrpSort
            variant={areQuickFilterPillsActive ? SelectVariant.ROUNDED : SelectVariant.DEFAULT}
          />
        </SrpSortContainer>
      </SrpSortMapContainer>
    </SrpResultsSortContainer>
  )
}
